/** Layout: rail + sidebar + content on desktop; content + bottom tabs on phones. */
import { useEffect, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  CalendarDays,
  ChevronsRight,
  Inbox,
  ListChecks,
  Menu as MenuIcon,
  Plus,
  WifiOff,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useIsDesktop, useOnline } from '@/lib/hooks';
import { usePendingCount, useUnreadCount } from '@/data/queries';
import { usePrefs, useUI } from '@/state/ui';
import { IconButton } from '@/components/ui/button';
import { Rail } from './Rail';
import { Sidebar } from './Sidebar';
import { registerServiceWorker, updateBadge } from '@/push/push';
import { SelectionBar } from '@/components/task/SelectionBar';

export function AppShell({ children }: { children: ReactNode }) {
  const desktop = useIsDesktop();
  const unread = useUnreadCount();
  useEffect(() => {
    void registerServiceWorker();
  }, []);
  useEffect(() => {
    void updateBadge(unread);
  }, [unread]);
  const collapsed = usePrefs((s) => s.sidebarCollapsed);
  const setCollapsed = usePrefs((s) => s.setSidebarCollapsed);
  if (desktop) {
    return (
      <div className="flex h-full">
        <Rail />
        {!collapsed && <Sidebar />}
        <main className="relative flex min-w-0 flex-1 flex-col bg-bg">
          {collapsed && (
            <IconButton
              label="Show sidebar"
              size="sm"
              className="absolute top-3 left-1 z-10"
              onClick={() => setCollapsed(false)}
            >
              <ChevronsRight className="size-4" />
            </IconButton>
          )}
          <OfflineBanner />
          {children}
          <SelectionBar />
        </main>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col">
      <OfflineBanner />
      <main className="relative flex min-h-0 flex-1 flex-col bg-bg">
        {children}
        <SelectionBar />
      </main>
      <BottomNav />
    </div>
  );
}

function OfflineBanner() {
  const online = useOnline();
  const pending = usePendingCount();
  if (online) return null;
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 bg-today/10 px-3 py-1.5 text-sm text-today"
    >
      <WifiOff className="size-4" />
      You're offline. Changes are saved on this device{pending ? ` (${pending} waiting)` : ''} and
      will sync when you reconnect.
    </div>
  );
}

function BottomNav() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const unread = useUnreadCount();
  const openQuickAdd = useUI((s) => s.openQuickAdd);
  const item = (
    to: string,
    label: string,
    Icon: typeof ListChecks,
    active: boolean,
    badge?: number,
  ) => (
    <Link
      to={to}
      className={cn(
        'relative flex flex-col items-center gap-[3px] pt-2 text-[11px] leading-[14px] font-medium',
        active ? 'text-primary' : 'text-ink-2',
      )}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className="size-[22px]" />
      {label}
      {!!badge && (
        <span className="absolute top-0.5 left-[54%] rounded-full bg-overdue px-1.5 text-[10px] leading-[14px] font-semibold text-white">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
  return (
    <nav
      className="safe-bottom grid shrink-0 grid-cols-5 border-t border-line bg-raised"
      style={{ minHeight: 'calc(64px + env(safe-area-inset-bottom, 0px))' }}
      aria-label="Main"
    >
      {item('/', 'My Tasks', ListChecks, path === '/' || path.startsWith('/t/'))}
      {item('/inbox', 'Inbox', Inbox, path.startsWith('/inbox'), unread)}
      <div className="flex justify-center pt-1">
        <button
          onClick={() => openQuickAdd({ tab: 'task' })}
          aria-label="Quick add"
          className="flex size-12 items-center justify-center rounded-full bg-primary text-on-primary shadow-[0_6px_16px_rgba(15,118,110,.35)]"
        >
          <Plus className="size-6" />
        </button>
      </div>
      {item('/calendar', 'Calendar', CalendarDays, path.startsWith('/calendar'))}
      {item(
        '/more',
        'More',
        MenuIcon,
        path.startsWith('/more') ||
          path.startsWith('/spaces') ||
          path.startsWith('/settings') ||
          path.startsWith('/l/') ||
          path.startsWith('/trash'),
      )}
    </nav>
  );
}
